import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, ViewChild } from '@angular/core';
import { WidgetService } from 'src/core/services/widget.service';
import { DeviceService } from 'src/core/services/device.service';
import { LoadingService } from 'src/core/services/loading.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { ChatService } from 'src/core/services/chat.service';
import { Table, TableModule } from 'primeng/table';
import { DaterangeService } from 'src/core/services/daterange.service';
import { defer, iif, map, merge, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { DataService } from 'src/core/services/data.service';
import { workareaSize } from 'src/core/types/workarea.types';
import { NgShuiModule } from '@shui/angular';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiResponse } from 'src/core/types/general.types';
import { SortEvent } from 'primeng/api';
import { FunctionalLocationOverview } from 'src/core/types/funcloc.types';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { AppConfigService } from 'src/core/services/appconfig.service';
import { BusinessPartnerAccessService } from 'src/core/services/business-partner-access.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-funcloc-details',
  imports: [CommonModule, TableModule, NgShuiModule, FormsModule, TranslocoModule],
  templateUrl: './funcloc-details.component.html',
  styleUrl: './funcloc-details.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FunclocDetailsComponent {

  //Table Variables
  @ViewChild('table') table: Table;

  constructor(
    private widgetService: WidgetService,
    private deviceService: DeviceService,
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private selectedContentService: SelectedContentService,
    private daterangeService: DaterangeService,
    private dataService: DataService,
    private appConfigService: AppConfigService,
    private bpAccessService: BusinessPartnerAccessService,
  ) { }

  screenInfo$ = this.deviceService.screenInfo$;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  deviceInformation: any;
  searchTerm: string = '';
  searchTermType: string = '';
  site: any[] = [];
  selectedRange: number;
  productNameLookup: any = {};

  workareaSize: workareaSize = "small";
  onMaximize = this.widgetService.onMaximize.bind(this.widgetService);
  onMinimize = this.widgetService.onMinimize.bind(this.widgetService);
  onExpand = (event: any) => {
    if(this.workareaSize !== "fullscreen") {
      this.widgetService.onExpand();
    } else {
      this.widgetService.$workareaSize.next("large");
    }
  }

  get showMachineDataColumns(): boolean {
    if (this.selectedContentService.selection.kind !== 'site' || !environment.businessPartnerMode) {
      return true;
    }

    return this.bpAccessService.access?.machineData ?? false;
  }

  get showServiceDataColumns(): boolean {
    if (this.selectedContentService.selection.kind !== 'site' || !environment.businessPartnerMode) {
      return true;
    }

    return this.bpAccessService.access?.serviceData ?? false;
  }

  get showServiceMetricsColumns(): boolean {
    return this.showServiceDataColumns && environment.enableServiceMetricsWidget;
  }

  changeRange = (range: number) => {
    if (this.selectedContentService.selection.kind === "site") {
      this.daterangeService.$selectedRangeFunctionLocationSite.next(range);
      return;
    }
    this.daterangeService.$selectedRangeFunctionLocation.next(range);
  };
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {

    this.loadingService.$isSiteLoading.subscribe((isSiteLoading) => {
      this.isLoading = isSiteLoading;
    });
    this.loadingService.$isSiteEmpty.subscribe((isSiteEmpty) => {
      this.isEmpty = isSiteEmpty;
    });
    this.loadingService.$isSiteError.subscribe((isSiteError) => {
      this.isError = isSiteError;
    });

    this.selectedSystemService.$siteoverview.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((siteData: FunctionalLocationOverview) => {
      if (siteData) {
        this.site = siteData.device_data.map(device => ({
          ...device,
          ivk: "Test Instrument Group"
        }));

        console.log(siteData.device_data.map(device => ({
          ...device,
          ivk: "Test Instrument Group"
        })))

      } else {
        this.site = [];

      }
    });
    //Need to know the current workarea size as well to display correct icons
    this.widgetService.$workareaSize.subscribe({
      next: (size: workareaSize) => {
        this.workareaSize = size;
      }
    });

    //Listening to Product Names
    this.appConfigService.config$.subscribe({
      next: (capabilities) => {
        this.productNameLookup = capabilities;
      }
    });

    //Listening to changed Daterange
    const range$ = this.selectedContentService.selection.kind === "site" ? this.daterangeService.$selectedRangeFunctionLocationSite : this.daterangeService.$selectedRangeFunctionLocation;

    // Trigger when range changes (normal), or when user clicks refresh (force fetch)
    const triggers$ = merge(
      range$.pipe(map(rangeValue => ({ rangeValue, force: false }))),
      this.selectedSystemService.$refreshSite.pipe(
        withLatestFrom(range$),
        map(([, rangeValue]) => ({ rangeValue, force: true }))
      )
    );

    triggers$.pipe(
      tap(() => {
        // ✅ Reset before API call
        this.loadingService.$isSiteLoading.next(true);
        this.loadingService.$isSiteError.next(false);
        this.site = null;
      }),
      switchMap(({ rangeValue, force }) => defer(() => {
        const now = new Date();
        const lastPulled = this.selectedSystemService.SitePullDate;
        const pulledFor = this.selectedSystemService.SitePulledFor;

        console.log('Last pulled date:', this.selectedContentService.currentSystem, this.selectedContentService.currentSite);

        const funcloc =
          this.selectedContentService.selection.kind !== "site" ?
            this.selectedContentService.currentSystem?.Functional_Location_ID : this.selectedContentService.currentSite?.Id;

        const shouldFetch =
          force ||
          !lastPulled ||
          (now.getTime() - new Date(lastPulled).getTime()) > 5 * 60 * 1000 ||
          pulledFor !== funcloc;

        return iif(
          () => shouldFetch,
          this.dataService.getFunctionalLocationOverview(funcloc).pipe(
            tap(({ response, status }: ApiResponse<FunctionalLocationOverview>) => {
              console.log(force ? 'Forced refresh of funcloc data' : 'Fetching new funcloc data');
              this.selectedSystemService.SitePullDate = now;
              this.selectedSystemService.SitePulledFor = funcloc;

              if (status === 200) {
                this.selectedSystemService.$siteoverview.next(response);
              }
              else if (status === 204) {
                this.selectedSystemService.$siteoverview.next(null);
                this.loadingService.$isSiteEmpty.next(true);
              }
            })
          ),
          this.selectedSystemService.$siteoverview.pipe(
            take(1),
            tap(() => console.log('Using cached parts data')),
            map((cached): ApiResponse<FunctionalLocationOverview> => ({
              response: cached,
              status: 200 // or some fixed value to simulate a successful cached response
            }))
          )
        ).pipe(
          map(({ response, status }: ApiResponse<FunctionalLocationOverview>) => ({ response, rangeValue }))
        );
      })),
      // 👇 Auto-unsubscribe when the component is destroyed
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      {
        next: ({ response, rangeValue }) => {

          this.selectedRange = rangeValue;

          let site_data = response?.device_data ?? [];

          console.log(this.productNameLookup);

          this.site = site_data.map(device => ({
            ...device,
            instrumentName: this.productNameLookup.filter(f => f.dataApiName === device.product_family)[0]?.instrumentName
          }));

          this.loadingService.$isSiteLoading.next(false);

        },
        error: (error) => {
          console.error('Error fetching spare parts data:', error);
          this.site = [];
          this.loadingService.$isSiteError.next(true);
          this.loadingService.$isSiteLoading.next(false);
        }
      }
    );
  }



  onSearchApply(event: any) {
    console.log('Search term applied:', event);
    this.searchTerm = event.value?.toLowerCase() || '';
  }

  highlight(text: string): string {
    if (!this.searchTerm || !text) return text;

    const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.replace(regex, '<mark class="hl">$1</mark>');
  }

  refresh() {
    this.selectedSystemService.$refreshSite.next();
  }

  customSort(event: SortEvent) {
    const field = event.field;
    const order = event.order;

    event.data.sort((a, b) => {
      let val1: any;
      let val2: any;

      switch (field) {

        case 'serial': // string field
          val1 = a.serial_number?.toLowerCase() ?? '';
          val2 = b.serial_number?.toLowerCase() ?? '';
          break;
        
        case 'instrumentName':
          val1 = a.instrumentName?.toLowerCase() ?? '';
          val2 = b.instrumentName?.toLowerCase() ?? '';
          break;

        case 'autocheck':
          val1 = a.system_health[this.selectedRange + ""].autochecks || 0;
          val2 = b.system_health[this.selectedRange + ""].autochecks || 0;
          break;
        case 'maintenance':
          val1 = a.system_health[this.selectedRange + ""].maintenance || 0;
          val2 = b.system_health[this.selectedRange + ""].maintenance || 0;
          break;
        case 'events':
          val1 = a.system_health[this.selectedRange + ""].error_events || 0;
          val2 = b.system_health[this.selectedRange + ""].error_events || 0;
          break;
        case 'qc':
          val1 = a.system_health[this.selectedRange + ""].qc || 0;
          val2 = b.system_health[this.selectedRange + ""].qc || 0;
          break;
        case 'proa':
          val1 = a.system_health[this.selectedRange + ""].proactive_alerts || 0;
          val2 = b.system_health[this.selectedRange + ""].proactive_alerts || 0;
          break;

        case 'noti':
          val1 = a.service_history[this.selectedRange + ""].notifications_open || 0;
          val2 = b.service_history[this.selectedRange + ""].notifications_open || 0;
          break;
        case 'visits':
          val1 = a.service_history[this.selectedRange + ""].site_visits || 0;
          val2 = b.service_history[this.selectedRange + ""].site_visits || 0;
          break;
        case 'parts':
          val1 = a.service_history[this.selectedRange + ""].spare_parts_used || 0;
          val2 = b.service_history[this.selectedRange + ""].spare_parts_used || 0;
          break;

        case 'cdi':
          val1 = a.service_metrics.cdi.current || 0;
          val2 = b.service_metrics.cdi.current || 0;
          break;
        case 'ci':
          val1 = a.service_metrics.ci.current || 0;
          val2 = b.service_metrics.ci.current || 0;
          break;

        default:
          val1 = a[field];
          val2 = b[field];
          break;
      }

      let result = 0;
      if (val1 < val2) result = -1;
      else if (val1 > val2) result = 1;

      return result * order;
    });
  }

  openWithParams(params: Record<string, string>) {
    // Create a URL object from the current location
    const url = new URL(window.location.href);

    // Remove all existing query params
    url.search = '';

    // Add or update params
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    // Open the new URL in a new browser tab
    window.open(url.toString(), "_blank");
  }

}
